<template>
  <v-dialog v-model="editDialog" class="the-app-dialogs" persistent max-width="600px">
    <v-card>
      <form @submit.prevent="validateBeforeSubmit($event)">
        <v-layout row class="no-select" style="background-color:#09132f">
          <h3 id="dialog-title" class="text-uppercase px-3 pt-3" style="text-xs-center letter-spacing:2px">
            <v-layout align-center>
              <v-icon light x-large class="rgba(0,0,0,0.3)--text pb-1">attach_money</v-icon>
              <span class="px-3">{{ $t('Edit Promo Card information') }}</span>
            </v-layout>
          </h3>
          <v-spacer />
          <v-card-actions>
            <v-btn fab icon dark @click="$emit('hidemodal')">
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-actions>
        </v-layout>

        <v-card-title class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="headline no-select pb-1">
              <v-layout justify-end></v-layout>
            </v-flex>

            <v-layout row wrap>
              <v-flex pa-2>
                <v-autocomplete
                  v-model="promoCardInfo.customer_id_fk"
                  :items="customersList"
                  :label="`${$t('Customer')} *`"
                  box
                  hide-details
                  background-color="white"
                  class="text-capitalize"
                  :error="errors.has('Customer')"
                  data-vv-name="Customer"
                  v-validate="'required'"
                />
              </v-flex>

              <v-flex xs1 pt-2 v-if="hasPermissions(['customers_store'])">
                <v-tooltip top>
                  <v-btn label @click="$emit('add-customer')" slot="activator" icon fab small color="green">
                    <v-icon color="white" size="25">add</v-icon>
                  </v-btn>
                  <span>{{ $t('Add new customer') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex xs4 pa-2>
                <v-text-field
                  v-model="promoCardInfo.promo_card_discount"
                  :label="`${$t('Discount')} *`"
                  box
                  suffix="%"
                  hide-details
                  background-color="white"
                  :error="errors.has('Discount')"
                  data-vv-name="Discount"
                  v-validate="'required|numeric'"
                />
              </v-flex>

              <v-flex xs12 pa-2>
                <v-autocomplete
                  v-model="promoCardInfo.promo_card_outlets"
                  :items="outletsList"
                  :label="`${$t('Outlet')} *`"
                  box
                  hide-details
                  multiple
                  chips
                  deletable-chips
                  background-color="white"
                  class="text-capitalize"
                  :error="errors.has('Outlet')"
                  data-vv-name="Outlet"
                  v-validate="'required'"
                  :menu-props="{ transition: 'slide-y-transition' }"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index < 3">
                      <span>{{ item.text }}</span>
                    </v-chip>
                    <span v-if="index === 3" class="grey--text caption"
                      >(+{{ promoCardInfo.promo_card_outlets.length - 3 }} others)</span
                    >
                  </template>
                  <template v-slot:prepend-item>
                    <v-list-tile ripple @click="toggle">
                      <v-list-tile-action>
                        <v-icon
                          :color="
                            promoCardInfo.promo_card_outlets != null
                              ? promoCardInfo.promo_card_outlets.length > 0
                                ? 'indigo darken-4'
                                : ''
                              : ''
                          "
                          >{{ icon }}</v-icon
                        >
                      </v-list-tile-action>
                      <v-list-tile-content>
                        <v-list-tile-title>Select All</v-list-tile-title>
                      </v-list-tile-content>
                    </v-list-tile>
                    <v-divider />
                  </template>
                </v-autocomplete>
              </v-flex>

              <v-flex xs12 class="px-2">
                <v-layout justify-start>
                  <v-flex sm3 md2 class="pt-3">
                    <v-chip
                      label
                      :color="promoCardInfo.promo_card_status === 'Active' ? 'success' : 'error'"
                      text-color="white"
                    >
                      <v-icon left tabindex="-1">{{
                        promoCardInfo.promo_card_status === 'Active' ? 'check_circle' : 'block'
                      }}</v-icon>
                      {{ promoCardInfo.promo_card_status === 'Active' ? $t('Active') : $t('Inactive') }}
                    </v-chip>
                  </v-flex>
                  <v-checkbox
                    v-model="promoCardInfo.promo_card_status"
                    :label="$t('Active')"
                    :true-value="'Active'"
                    :false-value="'Inactive'"
                    color="primary"
                    messages="Please note that if this option is unchecked this promo card will not be able to work with"
                  />
                </v-layout>
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card-title>
        <v-divider light />

        <v-card-actions class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="pb-2 px-2" style="color: #444;"
              >- {{ $t('Fields marked with asterisk ( * ) are required.') }}</v-flex
            >

            <v-layout justify-end>
              <v-btn outline color="primary" @click="$emit('hidemodal')">{{ $t('Cancel') }}</v-btn>
              <v-btn type="submit" color="success" :loading="btnUpdateLoading">
                <v-icon :right="$vuetify.rtl" :left="!$vuetify.rtl">save</v-icon>
                {{ $t('Update') }}
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'

export default {
  name: 'edit-component',

  props: {
    editDialog: { type: Boolean, required: true }
  },

  data() {
    return {
      btnUpdateLoading: false
    }
  },

  computed: {
    ...mapGetters({
      promoCardInfo: 'promoCards/getPromoCardInfo',
      outletsList: 'outlets/getFullOutletsList',
      customersList: 'customers/getFullCustomersList'
    }),

    selectAllOutlet() {
      if (this.promoCardInfo.promo_card_outlets != null && this.outletsList.length != null) {
        return this.promoCardInfo.promo_card_outlets.length === this.outletsList.length
      }
    },

    selectSomeOutlet() {
      if (this.promoCardInfo.promo_card_outlets != null) {
        return this.promoCardInfo.promo_card_outlets.length > 0 && !this.selectAllOutlet
      }
    },

    icon() {
      if (this.selectAllOutlet) return 'mdi-close-box'
      if (this.selectSomeOutlet) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    }
  },

  watch: {
    editDialog: {
      immediate: false,
      handler() {
        if (this.editDialog) {
          this.parseOutletStringToArray()
          this.addEscKeyListener()
        }
      }
    }
  },

  methods: {
    ...mapActions({
      updatePromoCardInfo: 'promoCards/updatePromoCardInfo'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    parseOutletStringToArray() {
      this.promoCardInfo.promo_card_outlets = this.promoCardInfo.promo_card_outlets.split(',').map(item => {
        return parseInt(item)
      })
    },

    addEscKeyListener() {
      document.addEventListener('keyup', e => {
        // if the 'ESC' button was pressed and this dialog is shown, hide it
        if (e.keyCode === 27 && this.editDialog) {
          this.$emit('hidemodal')
        }
      })
    },

    toggle() {
      this.$nextTick(() => {
        if (this.selectAllOutlet) {
          this.promoCardInfo.promo_card_outlets = []
        } else {
          this.promoCardInfo.promo_card_outlets = this.outletsList.slice()
        }
      })
    },

    modifyPromoCardOutlet(value) {
      if (value) {
        this.promoCardInfo.promo_card_outlets = 'All'
      } else {
        this.promoCardInfo.promo_card_outlets = []
      }
    },

    validateBeforeSubmit(event) {
      this.$validator
        .validateAll()
        .then(() => {
          if (!this.errors.any()) {
            this.btnUpdateLoading = true
            let formData = new FormData(event.target)
            formData.append('customer_id_fk', this.promoCardInfo.customer_id_fk)
            formData.append('promo_card_discount', this.promoCardInfo.promo_card_discount)
            formData.append('promo_card_status', this.promoCardInfo.promo_card_status)

            if (this.selectAllOutlet && this.promoCardInfo.promo_card_outlets.length > 1) {
              this.outletsList.forEach((outlet, index) => {
                formData.append('promo_card_outlets[' + [index] + ']', outlet.value)
              })
            } else {
              this.promoCardInfo.promo_card_outlets.forEach((outlet, index) => {
                formData.append('promo_card_outlets[' + [index] + ']', outlet)
              })
            }

            let payload = { id: this.promoCardInfo.promo_card_id, formData }
            this.updatePromoCardInfo(payload)
              .then(() => {
                this.btnUpdateLoading = false
                this.$emit('updated')
              })
              .catch(() => {
                this.btnUpdateLoading = false
              })
          }
        })
        .catch(() => {
          this.btnUpdateLoading = false
        })
    }
  }
}
</script>

<style scoped />
